import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Copy, Check, Terminal, Database } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const SQL_BLOCKS: { title: string; description: string; sql: string }[] = [
  {
    title: "1. Configurar URL das Edge Functions no banco (vault)",
    description:
      "Faz o pg_cron disparar as edge functions diretamente. Rode UMA VEZ no SQL Editor do Supabase. Substitua <PROJECT_REF> pelo seu (já preenchido).",
    sql: `-- Salva o URL base do projeto no vault para o pg_cron disparar edge functions
SELECT vault.create_secret('https://aqumdxgzmqacptvvrfid.supabase.co', 'p2hm_edge_url', 'Base URL das edge functions P2HM');`,
  },
  {
    title: "2. Configurar service_role key no banco (vault)",
    description:
      "Permite que o pg_cron autentique nas edge functions. Use a sua service_role key (Cloud → Settings → API). NUNCA exponha essa key no frontend. Em vez disso, use o botão 'Habilitar pg_cron automático' em Integrations — ele faz isso de forma segura via edge function.",
    sql: `-- ALTERNATIVA MANUAL (use apenas se não conseguir usar o botão em Integrations):
SELECT vault.create_secret('SUA_SERVICE_ROLE_KEY_AQUI', 'p2hm_service_key', 'Service role key para pg_cron');`,
  },
];

export default function AdminSetup() {
  const [copied, setCopied] = useState<number | null>(null);

  const copy = async (text: string, idx: number) => {
    await navigator.clipboard.writeText(text);
    setCopied(idx);
    toast.success("SQL copiado");
    setTimeout(() => setCopied(null), 1500);
  };

  return (
    <div className="min-h-screen bg-background text-foreground p-6">
      <div className="max-w-3xl mx-auto space-y-6">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <Link to="/" className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1">
            <ArrowLeft className="w-3 h-3" /> Voltar
          </Link>
          <Link
            to="/admin/dashboard"
            className="text-xs text-primary hover:underline inline-flex items-center gap-1"
          >
            Admin Dashboard →
          </Link>
        </div>

        <div>
          <h1 className="text-2xl font-display font-bold flex items-center gap-2">
            <Terminal className="w-6 h-6 text-primary" /> Admin Setup
          </h1>
          <p className="text-sm text-muted-foreground">
            Configurações de banco one-shot que precisam ser executadas pelo admin do projeto.
            Use o SQL Editor do backend Lovable Cloud.
          </p>
        </div>

        <div className="glass-panel p-4 rounded-lg border border-primary/20 text-xs space-y-2">
          <div className="flex items-center gap-2 text-primary font-semibold">
            <Database className="w-4 h-4" /> Recomendado
          </div>
          <p>
            Em vez de rodar SQL manual, vá em <Link to="/" className="underline text-primary">App → Integrations</Link> e clique em
            <strong> "Habilitar pg_cron automático"</strong>. Isso configura tudo via edge function segura.
          </p>
        </div>

        {SQL_BLOCKS.map((b, i) => (
          <div key={i} className="glass-panel rounded-lg border border-border overflow-hidden">
            <div className="px-4 py-3 border-b border-border flex items-start justify-between gap-3">
              <div>
                <h3 className="font-semibold text-sm">{b.title}</h3>
                <p className="text-xs text-muted-foreground mt-1">{b.description}</p>
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={() => copy(b.sql, i)}
                aria-label={`Copiar SQL ${i + 1}`}
                className="shrink-0 gap-1.5"
              >
                {copied === i ? <Check className="w-3.5 h-3.5 text-success" /> : <Copy className="w-3.5 h-3.5" />}
                {copied === i ? "Copiado" : "Copy"}
              </Button>
            </div>
            <pre className="p-4 text-[11px] font-mono bg-surface-2/40 overflow-auto">{b.sql}</pre>
          </div>
        ))}

        <div className="text-xs text-muted-foreground p-4 border border-border rounded-lg">
          <strong>Como executar:</strong> abra o backend (Cloud) → SQL Editor → cole → Run.
          Os comandos são idempotentes: se já existir, não duplica.
        </div>
      </div>
    </div>
  );
}
